import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import DropzoneComponent from 'react-dropzone-component';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import {
  doUploadProfilePicture,
  startProfilePhotoLoader,
  stopProfilePhotoLoader
} from '../../actions/manageUserProfilePicture';
import { demoRestriction, userRestriction } from '../../helpers/DemoRestriction/demoRestriction';

import showToaster from '../../helpers/toasterMessages/showToaster';
import { CameraIcon } from '../Common/Assets/SvgIconComponent/CommonSvgIcon';
import profileEditIcon from '/public/SiteIcons/editProfileIcon.svg';
import s from 'react-dropzone-component/styles/filepicker.css';
import t from '../../styles/profileDropzone.css';


const Dropzone = ({
  doUploadProfilePicture,
  startProfilePhotoLoader,
  stopProfilePhotoLoader,
  guestPicture,
  userId,
  maxUploadSize,
  isEditIcon,
  defaultMessage,
  containerClass,
  isAdmin,
  profileId,
  demoRestriction,
  userRestriction
}) => {
  const dropzoneRef = useRef(null);

  const handleDOMStyleAdjust = () => {
    const input = document.querySelector('.dz-hidden-input');
    if (input) {
      input.style.visibility = 'visible';
      input.style.opacity = '0';
      input.style.height = '100%';
      input.style.width = '100%';
      input.style.cursor = 'pointer';
    }
  };

  useEffect(() => {
    handleDOMStyleAdjust();
  });

  const handleSuccess = async (file, fromServer) => {
    const fileName = fromServer?.file?.filename;
    const isRestrict = await userRestriction({ restrictType: 'profile', restrictValue: profileId });
    if (isAdmin) {
      const isCheckRestrict = await demoRestriction({});
      if (isCheckRestrict) {
        dropzoneRef.current?.removeFile(file);
        return;
      }
    } else if (isRestrict) {
      showToaster({ messageId: 'demoWarning', toasterType: 'warning' });
      dropzoneRef.current?.removeFile(file);
      return;
    }
    if (fileName) {
      doUploadProfilePicture(fileName, guestPicture, userId);
    }
  };

  const handleAddedFile = async (file) => {
    const filetypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const isOnline = typeof window !== 'undefined' && window.navigator.onLine;
    const isRestrict = await userRestriction({ restrictType: 'profile', restrictValue: profileId });
    if (isAdmin) {
      const isCheckRestrict = await demoRestriction({});
      if (isCheckRestrict) {
        dropzoneRef.current?.removeFile(file);
        return;
      }
    } else if (isRestrict) {
      showToaster({ messageId: 'demoWarning', toasterType: 'warning' });
      dropzoneRef.current?.removeFile(file);
      return;
    }
    if (!isOnline) {
      showToaster({ messageId: 'offlineError', toasterType: 'error' });
      dropzoneRef.current?.removeFile(file);
      stopProfilePhotoLoader();
      return;
    }

    startProfilePhotoLoader();

    if (file?.size > 1024 * 1024 * parseInt(maxUploadSize)) {
      showToaster({ messageId: 'maximumUploadSize', toasterType: 'error' });
      dropzoneRef.current?.removeFile(file);
      stopProfilePhotoLoader();
      return;
    }

    if (!filetypes.includes(file.type)) {
      dropzoneRef.current?.removeFile(file);
      showToaster({ messageId: 'invalidImageFile', toasterType: 'error' });
      stopProfilePhotoLoader();
    }
  };

  const djsConfig = {
    dictDefaultMessage: '',
    addRemoveLinks: false,
    uploadMultiple: false,
    maxFilesize: maxUploadSize,
    acceptedFiles: 'image/jpeg,image/png',
    dictMaxFilesExceeded: 'Remove the existing image and try upload again',
    previewsContainer: false,
    hiddenInputContainer: '.dzInputContainer',
  };

  const componentConfig = {
    iconFiletypes: ['.jpg', '.png'],
    postUrl: '/uploadProfilePhoto'
  };

  const eventHandlers = {
    init: dz => (dropzoneRef.current = dz),
    success: handleSuccess,
    addedfile: handleAddedFile,
  };

  return (
    <div className={cx(!isEditIcon ? 'dashBoardImageUploadContainer' : 'dashBoardEditImageUploadContainer', containerClass)}>
      <div className={cx('dzInputContainer', !isEditIcon ? 'profileDropzoneContainer' : 'editProfileDropZoneContainer')}>
        <DropzoneComponent
          config={componentConfig}
          eventHandlers={eventHandlers}
          djsConfig={djsConfig}
        >
          {isEditIcon ? (
            <img src={profileEditIcon} alt="EditProfileIcon" />
          ) : (
            <CameraIcon className={'commonLinkColor'} />
          )}
          {defaultMessage && defaultMessage}
        </DropzoneComponent>
      </div>
    </div>
  );
};

Dropzone.propTypes = {
  doUploadProfilePicture: PropTypes.func.isRequired,
  startProfilePhotoLoader: PropTypes.func.isRequired,
  stopProfilePhotoLoader: PropTypes.func.isRequired,
  guestPicture: PropTypes.string,
  userId: PropTypes.string,
  maxUploadSize: PropTypes.number,
  isEditIcon: PropTypes.bool,
  defaultMessage: PropTypes.node
};

const mapState = (state) => ({
  maxUploadSize: state.siteSettings.data.maxUploadSize,
  profileId: state?.account?.data?.profileId,
});

const mapDispatch = {
  doUploadProfilePicture,
  startProfilePhotoLoader,
  stopProfilePhotoLoader,
  userRestriction,
  demoRestriction
};

export default injectIntl(
  withStyles(s, t)(
    connect(mapState, mapDispatch)(Dropzone)
  )
);
